import { AlertTriangle, ChevronRight, Info } from 'lucide-react';
import { useState } from 'react';

import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent } from '~/components/ui/dialog';
import { cn } from '~/utils/cn';

interface Props {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** `data-testid` for the dialog; derives `-title`, `-confirm-checkbox`, `-confirm-button`, `-step-{n}`. */
  testId?: string;
}

/** Pre-delivery confirmation modal for Google Merchant Center attribute rule setup.
 *  Walks the user through 4 steps and requires a checkbox acknowledgement before confirming. */
export function GMCPreDeliveryConfirmation({
  open,
  onCancel,
  onConfirm,
  testId = 'gmc-pre-delivery-dialog',
}: Props) {
  const [confirmed, setConfirmed] = useState(false);
  const [lastOpen, setLastOpen] = useState(open);
  // Render-time reset — avoids setState-in-effect. When the modal transitions
  // from closed to open the checkbox is cleared so the user must re-acknowledge.
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setConfirmed(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
    >
      <DialogContent
        data-testid={testId}
        className="flex max-h-[90vh] max-w-3xl flex-col gap-0 p-0"
      >
        {/* Scrollable body */}
        <div className="flex-1 space-y-6 overflow-y-auto px-8 pt-8 pb-4">
          {/* Header */}
          <div className="space-y-3 text-center">
            <h2 data-testid={`${testId}-title`} className="text-2xl leading-tight font-bold">
              Activate Lily enrichments in Google Merchant Center
            </h2>
            <p className="text-stage-text text-sm leading-5">
              Lily has delivered your enriched product data directly to your GMC account. One step
              remains: you need to set an attribute rule on your primary feed so GMC knows to use
              Lily's values in your ads.
            </p>
          </div>

          {/* Why this matters */}
          <div className="border-primary/30 bg-surface-blue space-y-2 rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Info className="text-primary h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="text-primary text-sm font-medium">Why this matters?</span>
            </div>
            <p className="text-stage-text text-sm leading-5">
              Google Shopping uses the attributes in your GMC catalog — titles, descriptions,
              product types, and more — to decide when and where your products appear. Without this
              rule, GMC ignores Lily's enrichments entirely and your ads continue running on your
              original, unenriched content.
            </p>
          </div>

          <hr className="border-border" />

          {/* Steps */}
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <StepBadge number={1} testId={`${testId}-step-1`} />
                <div className="space-y-1">
                  <p className="text-foreground text-sm font-medium">Open your primary feed</p>
                  <p className="text-stage-text text-sm leading-5">
                    In GMC, go to Settings → Data sources → Primary sources. Click on your current
                    main product feed to open it.
                  </p>
                </div>
              </div>
              <PathBox text="Settings → Data sources → Primary sources → [your main feed]" />
            </div>

            {/* Step 2 */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <StepBadge number={2} testId={`${testId}-step-2`} />
                <div className="space-y-1">
                  <p className="text-foreground text-sm font-medium">Go to Attribute rules</p>
                  <p className="text-stage-text text-sm leading-5">
                    Inside your primary feed, select the Data source setup tab, then click Attribute
                    rules.
                  </p>
                </div>
              </div>
              <PathBox text="Data source setup tab → Attribute rules" />
            </div>

            {/* Step 3 */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <StepBadge number={3} testId={`${testId}-step-3`} />
                <div className="space-y-1">
                  <p className="text-foreground text-sm font-medium">
                    Confirm the default rule includes Lily
                  </p>
                  <p className="text-stage-text text-sm leading-5">
                    GMC has a default rule that applies to all attributes at once. Check that the
                    Lily supplemental source is listed as the first source in this rule, with your
                    primary feed as the fallback.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <InfoSubBox
                  title="How the rule works?"
                  body="GMC applies sources in priority order across all attributes automatically — if Lily has a value for a product, it's used; if not, your original value is kept. You don't need to set rules attribute by attribute. If the Lily source is already listed first, no further changes are needed."
                />
                <InfoSubBox
                  title="If Lily isn't listed or isn't first?"
                  body="Click Add more ways to replace data on the default rule → select the Lily source → move it to the top of the priority order. Click Save as draft, then Apply changes."
                />
                <div className="border-warning/40 bg-warning-50 flex items-start gap-3 rounded-lg border p-4">
                  <AlertTriangle
                    className="text-warning mt-0.5 h-4 w-4 shrink-0"
                    aria-hidden="true"
                  />
                  <p className="text-stage-text text-sm leading-5">
                    Your CSM will confirm which attributes Lily has enriched for your catalog before
                    you complete this step.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex items-start gap-3">
              <StepBadge number={4} testId={`${testId}-step-4`} />
              <div className="space-y-1">
                <p className="text-foreground text-sm font-medium">Verify the rule is active</p>
                <p className="text-muted-foreground text-sm leading-5">
                  Confirm the rule shows as active in the Attribute rules tab. Changes typically
                  propagate within a few hours. Lily will run a validation check within 24 hours to
                  confirm enrichments are live across your catalog.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="flex items-center justify-between gap-4 border-t px-6 py-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="gmc-confirm-checkbox"
              data-testid={`${testId}-confirm-checkbox`}
              checked={confirmed}
              onCheckedChange={(v) => {
                setConfirmed(v === true);
              }}
              className="mt-0.5 cursor-pointer"
            />
            <label htmlFor="gmc-confirm-checkbox" className="cursor-pointer select-none">
              <p className="text-stage-text text-sm font-medium">
                I've confirmed attribute rules are set in GMC
              </p>
              <p className="text-stage-text text-xs leading-5">
                Lily will validate that enrichments are active within 24 hours.
              </p>
            </label>
          </div>
          <Button
            className={cn(
              'bg-gradient-btn shrink-0 gap-2',
              confirmed ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
            )}
            onClick={() => {
              if (!confirmed) return;
              onConfirm();
            }}
            aria-disabled={!confirmed}
            data-testid={`${testId}-confirm-button`}
          >
            Confirm Setup Complete
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StepBadge({ number, testId }: { number: number; testId: string }) {
  return (
    <span
      data-testid={testId}
      className="bg-surface-blue text-primary flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-medium"
    >
      {number}
    </span>
  );
}

function PathBox({ text }: { text: string }) {
  return (
    <div className="bg-surface-blue/30 rounded-lg border px-4 py-3">
      <p className="text-stage-text text-sm">{text}</p>
    </div>
  );
}

function InfoSubBox({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-surface-blue/30 space-y-1 rounded-lg border px-4 py-3">
      <p className="text-stage-text text-sm font-semibold">{title}</p>
      <p className="text-stage-text text-sm leading-5">{body}</p>
    </div>
  );
}
